import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { cacheScope, readCache } from "@/lib/ui-cache";
import { useCacheScope } from "@/hooks/use-cache-scope";

import {
  patchUnifiedPreferences,
  readUnifiedPreferences,
  type UnifiedPreferencesSnapshot,
} from "@/lib/unified-preferences";

export type SubmitShortcut = "enter" | "ctrl_enter";

export const DEFAULT_SUBMIT_SHORTCUT: SubmitShortcut = "enter";
export const DEFAULT_TIMEZONE = "Europe/Zurich";

export interface UserPreferences {
  submit_shortcut: SubmitShortcut;
  default_agent_id: string | null;
  timezone: string;
  [key: string]: unknown;
}

export interface UserPreferencesData {
  preferences: UserPreferences;
  updated_at: string | null;
}

export interface UserPreferencesPatch {
  submit_shortcut?: SubmitShortcut;
  default_agent_id?: string | null;
  timezone?: string;
}

export interface UseUserPreferencesReturn {
  data: UserPreferencesData | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** Fetches server state. Called on mount and when the dialog opens. */
  refresh: () => Promise<UserPreferencesData | null>;
  /** Sends only changed known keys; the server retains unknown JSON keys. */
  save: (patch: UserPreferencesPatch) => Promise<boolean>;
}

function normalizeResponse(payload: UnifiedPreferencesSnapshot): UserPreferencesData {
  const document = { ...payload.preferences };
  const submitShortcut = document.submit_shortcut;
  const defaultAgentId = document.default_agent_id;
  const timezone = document.timezone;

  return {
    preferences: {
      ...document,
      submit_shortcut:
        submitShortcut === "enter" || submitShortcut === "ctrl_enter"
          ? submitShortcut
          : DEFAULT_SUBMIT_SHORTCUT,
      default_agent_id:
        typeof defaultAgentId === "string" && defaultAgentId.trim()
          ? defaultAgentId
          : null,
      timezone: typeof timezone === "string" && timezone ? timezone : DEFAULT_TIMEZONE,
    },
    updated_at: payload.updatedAt,
  };
}

/**
 * Server-backed preference state. It reads once on mount so app-wide settings
 * such as the submit shortcut take effect without opening Preferences. The
 * dialog still refreshes on open, and new-chat creation can refresh if needed.
 * Browser storage is never used as a source of truth.
 */
export function useUserPreferences(
  getToken: () => Promise<string>,
): UseUserPreferencesReturn {
  const verifiedScope = useCacheScope();
  const previousScopeRef = useRef(verifiedScope);
  const { t } = useTranslation("chat");
  const [data, setData] = useState<UserPreferencesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialRefreshStartedRef = useRef(false);
  const refreshInFlightRef = useRef<Promise<UserPreferencesData | null> | null>(null);

  const refresh = useCallback((): Promise<UserPreferencesData | null> => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    const request = (async (): Promise<UserPreferencesData | null> => {
      setLoading(true);
      setError(null);
      try {
        const next = normalizeResponse(await readUnifiedPreferences(getToken));
        setData(next);
        return next;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t("preferences.errors.load"));
        return null;
      } finally {
        setLoading(false);
      }
    })();

    refreshInFlightRef.current = request;
    void request.finally(() => {
      if (refreshInFlightRef.current === request) {
        refreshInFlightRef.current = null;
      }
    });
    return request;
  }, [getToken, t]);

  useRefreshOnReconnect(refresh);

  useEffect(() => {
    // Strict Mode replays effects in development. Keep that replay from
    // starting a second initial request; concurrent callers share the first.
    if (initialRefreshStartedRef.current) return;
    initialRefreshStartedRef.current = true;
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (previousScopeRef.current === verifiedScope) return;
    previousScopeRef.current = verifiedScope;
    setData(null);
    refreshInFlightRef.current = null;
    if (!verifiedScope) return;
    let cancelled = false;
    void readCache<UnifiedPreferencesSnapshot>("displayPrefs", "display", "settings").then((snapshot) => {
      if (cancelled || cacheScope() !== verifiedScope || !snapshot) return;
      const shortcut = snapshot.preferences.submit_shortcut;
      if (shortcut !== "enter" && shortcut !== "ctrl_enter") return;
      setData({ preferences: { submit_shortcut: shortcut, default_agent_id: null, timezone: DEFAULT_TIMEZONE }, updated_at: null });
    });
    void refresh();
    return () => { cancelled = true; };
  }, [verifiedScope]);

  const save = useCallback(async (patch: UserPreferencesPatch): Promise<boolean> => {
    if (Object.keys(patch).length === 0) return true;
    setSaving(true);
    setError(null);
    try {
      const next = await patchUnifiedPreferences(
        getToken,
        { preferences: { ...patch } },
      );
      setData(normalizeResponse(next));
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("preferences.errors.save"));
      return false;
    } finally {
      setSaving(false);
    }
  }, [getToken, t]);

  return useMemo(() => ({
    data,
    loading,
    saving,
    error,
    refresh,
    save,
  }), [data, error, loading, refresh, save, saving]);
}
